import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OrgProvider } from "@/context/OrgContext";
import Shell from "@/components/Shell";
import Login from "@/views/Login";
import Dashboard from "@/views/Dashboard";
import Applications from "@/views/Applications";
import AppDetail from "@/views/AppDetail";
import NewScan from "@/views/NewScan";
import Findings from "@/views/Findings";
import FindingDetail from "@/views/FindingDetail";
import Exposure from "@/views/Exposure";
import WebTargets from "@/views/WebTargets";
import WebTargetDetail from "@/views/WebTargetDetail";
import WebScanLauncher from "@/views/WebScanLauncher";
import WebFindingDetail from "@/views/WebFindingDetail";
import Domains from "@/views/Domains";
import DomainDetail from "@/views/DomainDetail";
import RiskOverview from "@/views/RiskOverview";
import MonitoringInbox from "@/views/MonitoringInbox";
import ImpersonationBoard from "@/views/ImpersonationBoard";
import Watchers from "@/views/Watchers";
import Routing from "@/views/Routing";
import Watchlist from "@/views/Watchlist";
import SourcesHealth from "@/views/SourcesHealth";
import ChangePassword from "@/views/ChangePassword";
import AdminPanel from "@/views/AdminPanel";
import AcceptInvite from "@/views/AcceptInvite";
import ResetPassword from "@/views/ResetPassword";
import ExposureReport from "@/views/ExposureReport";

const Loading = ({ label }) => (
  <div className="min-h-screen flex items-center justify-center text-[#8A8175]">{label}</div>
);

function RequirePerm({ cap, level = "view", children }) {
  const { can } = useAuth();
  if (!can(cap, level)) return <Navigate to="/" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin()) return <Navigate to="/" replace />;
  return children;
}

function Protected() {
  const { user, ready } = useAuth();
  if (!ready) return <Loading label="Loading CyberLens…" />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <OrgProvider>
      <Shell>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/applications" element={<RequirePerm cap="appsec.applications"><Applications /></RequirePerm>} />
          <Route path="/applications/:id" element={<RequirePerm cap="appsec.applications"><AppDetail /></RequirePerm>} />
          <Route path="/scan" element={<RequirePerm cap="appsec.scans" level="manage"><NewScan /></RequirePerm>} />
          <Route path="/findings" element={<RequirePerm cap="appsec.findings"><Findings /></RequirePerm>} />
          <Route path="/findings/:id" element={<RequirePerm cap="appsec.findings"><FindingDetail /></RequirePerm>} />
          <Route path="/exposure" element={<RequirePerm cap="appsec.exposure"><Exposure /></RequirePerm>} />
          <Route path="/web/targets" element={<RequirePerm cap="web.targets"><WebTargets /></RequirePerm>} />
          <Route path="/web/targets/:id" element={<RequirePerm cap="web.targets"><WebTargetDetail /></RequirePerm>} />
          <Route path="/web/scan" element={<RequirePerm cap="web.scans" level="manage"><WebScanLauncher /></RequirePerm>} />
          <Route path="/web/findings/:id" element={<RequirePerm cap="web.findings"><WebFindingDetail /></RequirePerm>} />
          <Route path="/attack/domains" element={<RequirePerm cap="attack.domains"><Domains /></RequirePerm>} />
          <Route path="/attack/domains/:id" element={<RequirePerm cap="attack.domains"><DomainDetail /></RequirePerm>} />
          <Route path="/attack/risk" element={<RequirePerm cap="attack.domains"><RiskOverview /></RequirePerm>} />
          <Route path="/monitoring/inbox" element={<RequirePerm cap="monitor.alerts"><MonitoringInbox /></RequirePerm>} />
          <Route path="/monitoring/impersonation" element={<RequirePerm cap="monitor.alerts"><ImpersonationBoard /></RequirePerm>} />
          <Route path="/monitoring/watchers" element={<RequirePerm cap="monitor.watchers"><Watchers /></RequirePerm>} />
          <Route path="/monitoring/routing" element={<RequirePerm cap="monitor.watchers"><Routing /></RequirePerm>} />
          <Route path="/monitoring/watchlist" element={<RequirePerm cap="monitor.watchers"><Watchlist /></RequirePerm>} />
          <Route path="/monitoring/sources" element={<RequirePerm cap="monitor.sources"><SourcesHealth /></RequirePerm>} />
          <Route path="/account/password" element={<ChangePassword />} />
          <Route path="/admin" element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
    </OrgProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/monitoring/exposure-report" element={<ExposureReport />} />
          <Route path="/*" element={<Protected />} />
        </Routes>
        <Toaster theme="light" position="top-right"
          toastOptions={{ style: { background: "#fff", border: "1px solid #ECE6DD", color: "#323232", fontFamily: "Cambria, Gelasio, Georgia, serif" } }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
